// frontend/src/api/axiosConfig.js
//
// The single source of truth for where the API lives and how a request proves
// who it is. Twelve files used to repeat this line:
//
//   const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5150/api';
//
// and eleven built their own Bearer header. Changing how auth works meant
// editing a dozen files and noticing none of them — which is the failure mode
// this consolidation removes.
//
// Two exports, for two different situations:
//
//   default (axiosInstance)  the configured client: base URL, 10s timeout, a
//                            token on every request, and logout-on-401.
//   API_BASE_URL / authHeader  the same base URL and header for callers that
//                            still use bare axios.
//
// The second exists because the two groups do NOT behave the same, and this
// change deliberately did not paper over that. Files on bare axios have no
// timeout and do not redirect on 401; moving them onto axiosInstance would
// impose a 10-second limit on requests that currently have none (logo uploads,
// the platform dashboard) and would start throwing users to /login on a 401
// they presently just surface as an error. That is a behaviour change and
// belongs in its own reviewed step, not smuggled into a de-duplication.
import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5150/api';

// Reads the token at CALL time, not at import time — a login that happens after
// this module is first imported must still be picked up.
export const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to every request
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
